import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Locale, ParticipantFieldScope, ParticipantFieldType } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddParticipantFieldMutation from './AddParticipantFieldMutation';
import ParticipantFieldForm from './ParticipantFieldForm';
import UI from '../../../../common/UI';
import getParticipantFieldSchema from './participantFieldSchema';
import useMutation from '../../../../api/useMutation';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

interface Event {
  id: string;
  locale: Locale;
}

export interface AddParticipantFieldFormProps {
  event: Event;
  scope: ParticipantFieldScope;
  closeTo?: string;
  onSuccess?: () => void;
  refetchQueries?: DocumentNode[];
}

const AddParticipantFieldForm = ({
  event, scope, closeTo, onSuccess, refetchQueries,
}: AddParticipantFieldFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: {
      title: '',
      title_translations: [],
      description_translations: [],
      type: ParticipantFieldType.short_text,
      scope,
      required: true,
      unique: false,
      choices: [],
      tickets: [],
      products: [],
      promotions: [],
      config: null,
    },
  });

  const [addParticipantField, { loading, error }] = useMutation(
    AddParticipantFieldMutation,
    {
      variables: {
        input: {
          event: { id: event.id },
          ...form.values,
          config: form.values.config ? JSON.stringify(form.values.config) : null,
        },
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.field_added'));
      },
      onError: () => {
        scrollTo('AddParticipantFieldForm', { gutter: 1 });
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const { valid, errors } = useValidation({
    schema: getParticipantFieldSchema(null),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Card sc={{ outline: 'gray.200' }} id="AddParticipantFieldForm">
      <UI.Form onSubmit={() => addParticipantField()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <ParticipantFieldForm form={form} event={event} errors={errors} />

          <UI.HR />

          <UI.FormButtons
            form={form}
            submitLabel={t('add_field')}
            closeTo={closeTo}
            loading={loading}
            valid={valid}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Card>
  );
};

export default AddParticipantFieldForm;
